import React, { FC, useState, useEffect } from "react";
import useCRM from "../../hooks/useCRM";
import { Link } from "react-router-dom";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import Select from "react-select";
import constants from "../../constants";
import LoadingSpinbox from "../../components/UICore/LoadingSpinbox";

const Crm: FC = () => {
  const { users, loading, error, searchUsers } = useCRM();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [filterByMustHave, setFilterByMustHave] = useState<string[]>([]);
  const [filterByMustNotHave, setFilterByMustNotHave] = useState<string[]>([]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  useEffect(() => {
    searchUsers(searchQuery, sortBy, filterByMustHave, filterByMustNotHave);
  }, [searchQuery, sortBy, filterByMustHave, filterByMustNotHave, searchUsers]);

  return (
    <div className="flex flex-col gap-2 bg-gray-50 mb-8">
      <div className="px-4">
        <h2 className="text-4xl font-bold">Curate - CRM</h2>
        <div className="flex justify-between items-start mb-4">
          <form onSubmit={handleSearch} className="w-1/3">
            <div className="flex gap-2">
              <div className="w-64">
                <label className="block mb-2">Search:</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search users..."
                    className="border p-2 rounded flex-1"
                  />
                  <button
                    type="submit"
                    className="bg-blue-500 text-white p-2 rounded"
                  >
                    Search
                  </button>
                </div>
              </div>
            </div>
          </form>

          <div className="flex gap-4">
            <div className="w-64">
              <label className="block mb-2">Sort by:</label>
              <Select
                options={[
                  { value: "created_at", label: "Created At" },
                  {
                    value: "most_recent_message_recieved_timestamp",
                    label: "Most Recent Message",
                  },
                  {
                    value: "last_requested_more_matches",
                    label: "Last Requested Matches",
                  },
                  {
                    value: "last_viewed_converting_step",
                    label: "Last Viewed Converting Step",
                  },
                ]}
                value={{ value: sortBy, label: sortBy.replace(/_/g, " ") }}
                onChange={(option) => option && setSortBy(option.value)}
              />
            </div>
            <div className="w-64">
              <label className="block mb-2">Filter by (must have):</label>
              <Select
                isMulti
                options={
                  constants.EVENTS_TO_FILTER_BY.map((event) => ({
                    value: event,
                    label: event,
                  })) as { value: string; label: string }[]
                }
                value={filterByMustHave.map((event) => ({
                  value: event,
                  label: event,
                }))}
                onChange={(options) =>
                  setFilterByMustHave(options.map((option) => option.value))
                }
              />
            </div>
            <div className="w-64">
              <label className="block mb-2">Filter by (must not have):</label>
              <Select
                isMulti
                options={
                  constants.EVENTS_TO_FILTER_BY.map((event) => ({
                    value: event,
                    label: event,
                  })) as { value: never; label: never }[]
                }
                value={filterByMustNotHave.map((event) => ({
                  value: event,
                  label: event,
                }))}
                onChange={(options) =>
                  setFilterByMustNotHave(options.map((option) => option.value))
                }
              />
            </div>
          </div>
        </div>
        {loading && (
          <div className="py-8">
            <LoadingSpinbox textBelow="Loading CRM data..." />
          </div>
        )}
        {error && <p className="text-red-500">{error}</p>}
        {/* {!loading && !error && ( */}
        <div className="flex flex-col gap-4">
          <h3 className="text-2xl font-bold">Users ({users.length})</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {users.map((user) => (
              <Link
                key={user.user_id}
                to={`/crm/${user.user_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <div
                  className={`h-full rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden border border-gray-200 ${
                    user.events.includes("event_match_sent")
                      ? "bg-gradient-to-br from-green-50 to-green-100"
                      : "bg-gradient-to-br from-white to-gray-50"
                  }`}
                >
                  <div className="p-5">
                    <div className="flex justify-between items-start mb-3">
                      <span className="text-xs text-gray-700 bg-yellow-100 border border-yellow-700 px-2 py-1 rounded-full">
                        {new Date(user.created_at).toLocaleDateString("en-US", {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="text-lg font-extrabold text-gray-900">
                          {user.name} {user.last_name}
                        </div>
                        <span className="text-gray-400">•</span>
                        <span className="font-medium text-gray-700">
                          {user.computed_age}
                          {user.gender === "male" ? "M" : "F"}
                        </span>
                        <span className="text-gray-400">•</span>
                        <span className="font-bold text-gray-800">
                          {user.signup_location}
                        </span>
                      </div>

                      <div className="pt-2 border-t border-gray-100">
                        <div className="text-xs text-gray-600">
                          {user.most_recent_event
                            ? user.most_recent_event.replace("event_", "")
                            : "No recent event"}
                        </div>

                        {user.most_recent_message_recieved_timestamp && (
                          <div className="text-xs text-gray-500 mt-2">
                            Last message:{" "}
                            {new Date(
                              user.most_recent_message_recieved_timestamp,
                            ).toLocaleDateString("en-US", {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
        {/* )} */}
      </div>
    </div>
  );
};

export default withAuthenticationRequired(Crm, {
  returnTo: "/",
  onRedirecting: () => <Crm />,
});

// export default Crm;
